import type { ReactNode } from "react";

export type ModuleWidth = "full" | "half" | "rail";
export type ModuleTone = "card" | "bare" | "accent";

type Props = {
  /** When false the entire wrapper — heading, padding, spacing — is removed from the DOM. */
  active: boolean;
  /** Visually hidden headings are still announced; omit entirely for decorative shells. */
  title?: string;
  titleVisible?: boolean;
  action?: ReactNode;
  width?: ModuleWidth;
  tone?: ModuleTone;
  id?: string;
  className?: string;
  children: ReactNode;
};

const toneClass: Record<ModuleTone, string> = {
  card: "bg-card rounded-2xl p-4 md:p-5 shadow-card",
  accent: "rounded-2xl p-4 md:p-5 shadow-card bg-primary/10 border border-primary/20",
  bare: "",
};

/**
 * The single wrapper every optional profile module renders through.
 *
 * - Renders nothing at all when the module is not ready (no shell, no heading,
 *   no margin, no divider, no placeholder height).
 * - Full width on small screens regardless of the requested desktop placement.
 * - Never produces an empty grid cell: the parent lays children out with flex/gap
 *   so a removed module simply collapses and its neighbours move up.
 */
const ProfileModuleShell = ({
  active,
  title,
  titleVisible = true,
  action,
  width = "full",
  tone = "card",
  id,
  className = "",
  children,
}: Props) => {
  if (!active) return null;

  const widthClass =
    width === "half"
      ? "w-full min-w-0"
      : width === "rail"
      ? "w-full min-w-0"
      : "w-full min-w-0 max-w-full";

  return (
    <section id={id} className={`${widthClass} ${toneClass[tone]} ${className}`.trim()}>
      {title && (
        <div className={`flex items-center justify-between gap-3 flex-wrap ${titleVisible ? "mb-3 md:mb-4" : ""}`}>
          <h2
            className={
              titleVisible
                ? "text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                : "sr-only"
            }
          >
            {title}
          </h2>
          {action}
        </div>
      )}
      {children}
    </section>
  );
};

export default ProfileModuleShell;
